export const NEARBY_SHOP_RADIUS_KM = 100;

export function parseCoord(value) {
  if (value === "" || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** True only for real map coordinates (rejects 0,0 and out-of-range values). */
export function isValidGeoCoord(lat, lng) {
  const aLat = parseCoord(lat);
  const aLng = parseCoord(lng);
  if (aLat == null || aLng == null) return false;
  if (aLat === 0 && aLng === 0) return false;
  if (Math.abs(aLat) > 90 || Math.abs(aLng) > 180) return false;
  return true;
}

export function haversineDistanceKm(lat1, lng1, lat2, lng2) {
  const aLat = parseCoord(lat1);
  const aLng = parseCoord(lng1);
  const bLat = parseCoord(lat2);
  const bLng = parseCoord(lng2);
  if (aLat == null || aLng == null || bLat == null || bLng == null) return null;

  const toRad = (v) => (v * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

const pincodeCache = new Map();
const pincodeValidationCache = new Map();

/** Dev-only: browser console + backend terminal (POST /api/config/dev-log). */
export function logShopGeo(tag, message, data) {
  if (typeof console !== 'undefined') {
    console.log(`[ShopLocation:${tag}]`, message, data ?? '');
  }
  if (
    typeof window !== 'undefined'
    && process.env.NODE_ENV !== 'production'
  ) {
    fetch('/api/config/dev-log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tag: `ShopLocation:${tag}`, message, data }),
      keepalive: true,
    }).catch(() => {});
  }
}

/** Indian PIN: 6 digits, first digit 1–9. */
export function isIndianPinFormat(pincode) {
  const pin = String(pincode || '').replace(/\D/g, '').slice(0, 6);
  return pin.length === 6 && pin[0] !== '0';
}

export async function validateIndianPincode(pincode) {
  const pin = String(pincode || '').replace(/\D/g, '').slice(0, 6);
  if (!isIndianPinFormat(pin)) {
    return { valid: false, message: 'Enter a valid 6-digit Indian PIN code' };
  }
  if (pincodeValidationCache.has(pin)) return pincodeValidationCache.get(pin);

  try {
    const response = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
    const payload = await response.json();
    const entry = Array.isArray(payload) ? payload[0] : payload;
    const offices = entry?.PostOffice;
    if (entry?.Status === 'Success' && Array.isArray(offices) && offices.length > 0) {
      const primary = offices[0];
      const result = {
        valid: true,
        district: String(primary.District || '').trim(),
        state: String(primary.State || '').trim(),
        message: '',
      };
      pincodeValidationCache.set(pin, result);
      return result;
    }
    const result = { valid: false, message: 'This PIN code is not valid. Please check and try again.' };
    pincodeValidationCache.set(pin, result);
    return result;
  } catch {
    return { valid: null, message: 'Could not verify PIN code. Check your connection and try again.' };
  }
}

export async function lookupPincodeCoordinates(pincode) {
  const pin = String(pincode || "").replace(/\D/g, "").slice(0, 6);
  if (pin.length !== 6) return null;
  if (pincodeCache.has(pin)) return pincodeCache.get(pin);

  try {
    const response = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
    const payload = await response.json();
    const offices = Array.isArray(payload?.PostOffice) ? payload.PostOffice : [];
    for (const office of offices) {
      const lat = parseCoord(office?.Latitude);
      const lng = parseCoord(office?.Longitude);
      if (isValidGeoCoord(lat, lng)) {
        const coords = { lat, lng };
        pincodeCache.set(pin, coords);
        return coords;
      }
    }
  } catch {
    // Ignore lookup failures.
  }

  pincodeCache.set(pin, null);
  return null;
}

/** Above this (metres), browser GPS is treated as unreliable for browse/nearby — prefer IP there only. */
export const MAX_GPS_ACCURACY_M = 3000;

/** Above this (metres), reverse-geocoded city/PIN must not auto-fill shop address fields. */
export const SHOP_GPS_AUTO_FILL_ACCURACY_M = 100;

/** Above this (metres), device GPS is too coarse — use address/PIN geocoding instead. */
export const SHOP_GPS_UNRELIABLE_ACCURACY_M = 50000;

/**
 * Precise GPS threshold for partner signup.
 * At or below this, keep the existing mobile live-location flow.
 * Above this (or on desktop / denied), open Desktop Location Fallback.
 */
export const SHOP_GPS_PRECISE_ACCURACY_M = 100;

/** Mobile live location: reject fixes worse than this (avoids wrong village labels). */
export const SHOP_GPS_MOBILE_ACCEPT_ACCURACY_M = 150;

/** Browser sentinel when location is unavailable (~half Earth circumference). */
export const SHOP_GPS_INVALID_ACCURACY_M = 500000;

/** Typical IP geolocation radius (city-level). */
export const NETWORK_LOCATION_ACCURACY_M = 25000;

export function formatGpsAccuracyMeters(accuracyM) {
  const m = Math.round(Number(accuracyM) || 0);
  if (m >= 1000) {
    const km = m / 1000;
    return km >= 10 ? `±${Math.round(km)} km` : `±${km.toFixed(1)} km`;
  }
  return `±${m} m`;
}

async function fetchWithTimeout(url, ms = 6000) {
  if (typeof AbortController === 'undefined') {
    return fetch(url);
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  try {
    return await fetch(url, { signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Resolve approximate location from the user's login network (IP).
 */
export async function lookupLoginLocationFromIp() {
  const providers = [
    async () => {
      const response = await fetchWithTimeout('https://ipwho.is/');
      if (!response.ok) throw new Error('ipwho failed');
      const data = await response.json();
      if (!data.success) throw new Error(data.message || 'ipwho failed');
      return data;
    },
    async () => {
      const response = await fetchWithTimeout('https://get.geojs.io/v1/ip/geo.json');
      if (!response.ok) throw new Error('geojs failed');
      return response.json();
    },
    async () => {
      const response = await fetchWithTimeout('https://ipapi.co/json/');
      if (!response.ok) throw new Error('ipapi failed');
      return response.json();
    },
    async () => {
      const response = await fetchWithTimeout('https://freeipapi.com/api/json');
      if (!response.ok) throw new Error('freeipapi failed');
      const data = await response.json();
      return {
        latitude: data.latitude,
        longitude: data.longitude,
        city: data.cityName,
        region: data.regionName,
        country: data.countryName,
      };
    },
  ];

  for (let i = 0; i < providers.length; i += 1) {
    const providerName = ['ipwho.is', 'geojs.io', 'ipapi.co', 'freeipapi.com'][i] || `provider-${i}`;
    try {
      const data = await providers[i]();
      const lat = parseCoord(data.latitude);
      const lng = parseCoord(data.longitude);
      if (!isValidGeoCoord(lat, lng)) {
        logShopGeo('ip', `${providerName}: invalid coordinates`, { lat, lng });
        continue;
      }
      const city = String(data.city || '').trim();
      const region = String(data.region || data.region_name || '').trim();
      const country = String(data.country || data.country_name || '').trim();
      const result = {
        lat,
        lng,
        accuracy: NETWORK_LOCATION_ACCURACY_M,
        source: 'network',
        city,
        region,
        label: [city, region, country === 'India' || country === 'IN' ? '' : country]
          .filter(Boolean)
          .join(', '),
      };
      logShopGeo('ip', `${providerName}: resolved`, result);
      return result;
    } catch (err) {
      logShopGeo('ip', `${providerName}: failed`, { error: err?.message || String(err) });
    }
  }
  logShopGeo('ip', 'All IP geolocation providers failed');
  return null;
}

/**
 * Best location for browse/nearby: precise GPS when available, otherwise network (IP).
 * Do not use this for partner shop registration — IP is often wrong (e.g. carrier hub city).
 */
export async function resolveSignupLocation() {
  const ipPromise = lookupLoginLocationFromIp();

  let gpsResult = null;
  let gpsError = null;
  if (typeof navigator !== 'undefined' && navigator.geolocation) {
    try {
      const pos = await getAccuratePosition({ timeout: 15000, targetAccuracy: 500, maximumAge: 120000 });
      const accuracy = Math.round(pos.coords.accuracy);
      gpsResult = {
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy,
        source: 'gps',
      };
    } catch (err) {
      gpsError = err;
    }
  }

  const ipResult = await ipPromise;

  if (gpsResult && gpsResult.accuracy <= MAX_GPS_ACCURACY_M) {
    return gpsResult;
  }
  if (ipResult) {
    return ipResult;
  }
  if (gpsResult) {
    return gpsResult;
  }

  const error = gpsError || new Error('Could not detect location');
  if (gpsError?.code != null) error.code = gpsError.code;
  throw error;
}

/**
 * Detect a desktop browser (no mobile GPS chip) via the user agent.
 * Desktops usually can't reach fine GPS accuracy, so they need looser targets.
 */
export function isDesktopBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(ua);
  return !isMobile;
}

/**
 * GPS-only location for partner shop registration.
 * IP/network geolocation is excluded on mobile — carrier IPs (e.g. Jio) often resolve to the wrong city.
 * On desktop, GPS is coarse or unavailable, so we relax accuracy and fall back to IP geolocation.
 */
export async function resolveShopGpsLocation() {
  if (typeof navigator === 'undefined' || !navigator.geolocation) {
    const err = new Error('Geolocation not supported');
    err.code = 2;
    throw err;
  }

  const desktop = isDesktopBrowser();
  logShopGeo('start', 'resolveShopGpsLocation', { desktop });

  const positionOptions = desktop
    ? {
        timeout: 15000,
        targetAccuracy: 1000,
        maximumAge: 60000,
        allowCoarseFallback: true,
        minWaitMs: 2000,
      }
    : {
        timeout: 45000,
        targetAccuracy: 50,
        maximumAge: 0,
        allowCoarseFallback: false,
        minWaitMs: 10000,
      };

  let pos = null;
  let gpsError = null;
  try {
    pos = await getAccuratePosition(positionOptions);
  } catch (err) {
    gpsError = err;
  }

  if (pos) {
    const accuracy = Math.round(pos.coords.accuracy);
    const lat = pos.coords.latitude;
    const lng = pos.coords.longitude;

    const coordsValid = isValidGeoCoord(lat, lng);
    const accuracyUsable =
      Number.isFinite(accuracy) && accuracy > 0 && accuracy < SHOP_GPS_INVALID_ACCURACY_M;
    const accuracyReliable = accuracy <= SHOP_GPS_UNRELIABLE_ACCURACY_M;

    if (coordsValid && accuracyUsable && accuracyReliable) {
      logShopGeo('gps', 'Using browser GPS fix', { lat, lng, accuracy, desktop });
      return {
        lat,
        lng,
        accuracy,
        source: 'gps',
      };
    }
    logShopGeo('gps', 'GPS fix unusable or too coarse; trying IP fallback', {
      coordsValid,
      accuracyUsable,
      accuracyReliable,
      accuracy,
      desktop,
    });
    // Usable GPS fix not obtained — fall through to IP geolocation below.
  } else if (gpsError) {
    logShopGeo('gps', 'Browser geolocation failed', {
      code: gpsError?.code,
      message: gpsError?.message,
      desktop,
    });
  }

  // If browser GPS fails or is unusable (common on desktop/PC, indoors, or
  // when precise location is off), fall back to approximate IP geolocation so
  // the shop location still resolves. The PIN-vs-city check keeps it honest.
  const ipResult = await lookupLoginLocationFromIp();
  if (ipResult && isValidGeoCoord(ipResult.lat, ipResult.lng)) {
    logShopGeo('ip', 'Using IP geolocation fallback', {
      lat: ipResult.lat,
      lng: ipResult.lng,
      accuracy: ipResult.accuracy,
      label: ipResult.label,
    });
    return {
      lat: ipResult.lat,
      lng: ipResult.lng,
      accuracy: ipResult.accuracy,
      source: 'network',
    };
  }

  logShopGeo('error', 'GPS and IP location both failed', {
    gpsError: gpsError?.message,
    desktop,
  });
  const error = gpsError || new Error('Could not detect location');
  if (gpsError?.code != null) error.code = gpsError.code;
  throw error;
}

/**
 * Collect the best available GPS fix (waits briefly for accuracy to improve).
 */
export function getAccuratePosition(options = {}) {
  const {
    timeout = 12000,
    targetAccuracy = 80,
    maximumAge = 60000,
    allowCoarseFallback = true,
    minWaitMs = 0,
  } = options;

  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'));
      return;
    }

    let best = null;
    let watchId = null;
    let finished = false;
    let usingHighAccuracy = true;
    const startedAt = Date.now();

    const finish = (result, error) => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      clearTimeout(lowAccuracyTimer);
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      if (result) resolve(result);
      else reject(error || new Error('Could not get location'));
    };

    const canSettle = () => Date.now() - startedAt >= minWaitMs;

    const settleWithBest = () => {
      if (best) finish(best);
    };

    const consider = (pos) => {
      if (!best || pos.coords.accuracy < best.coords.accuracy) best = pos;
      if (pos.coords.accuracy <= targetAccuracy && canSettle()) finish(pos);
    };

    const startWatch = (enableHighAccuracy) => {
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      watchId = navigator.geolocation.watchPosition(
        consider,
        (err) => {
          if (err?.code === 1) {
            finish(null, err);
            return;
          }
          if (best && canSettle()) {
            settleWithBest();
            return;
          }
          if (usingHighAccuracy && enableHighAccuracy) {
            usingHighAccuracy = false;
            startWatch(false);
            return;
          }
          if (!best) finish(null, err);
        },
        { enableHighAccuracy, maximumAge, timeout: timeout + 5000 }
      );
    };

    const timer = setTimeout(() => {
      if (best) {
        // Do not accept a very coarse fix when the caller asked for precise GPS.
        if (!allowCoarseFallback && Number(best.coords.accuracy) > Math.max(targetAccuracy * 3, 150)) {
          const err = new Error('GPS accuracy too low');
          err.code = 3;
          err.accuracy = best.coords.accuracy;
          finish(null, err);
          return;
        }
        settleWithBest();
      } else {
        const err = new Error('Location timed out');
        err.code = 3;
        finish(null, err);
      }
    }, timeout);

    const lowAccuracyTimer = allowCoarseFallback
      ? setTimeout(() => {
        if (best || finished) return;
        navigator.geolocation.getCurrentPosition(
          consider,
          () => {},
          { enableHighAccuracy: false, maximumAge: 300000, timeout: 8000 }
        );
      }, 2500)
      : null;

    navigator.geolocation.getCurrentPosition(
      consider,
      () => {},
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
    );

    startWatch(true);
  });
}
